/**
 * MapInteractionController Component
 * Manage map interactions based on editing mode
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1187-1216, 30 lines)
 *
 * Features:
 * - Disable double-click zoom in edit mode (prevents accidental zoom)
 * - Set cursor to crosshair in edit mode for precision
 * - Keep scroll wheel zoom enabled
 * - Keep touch zoom enabled for mobile
 * - Restore default interactions when not editing
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

export interface MapInteractionControllerProps {
  /** Whether in editing mode (drawing polygons, placing modules, etc.) */
  isEditing: boolean;
}

/**
 * Control map interaction behavior based on editing state
 *
 * When editing:
 * - Disables double-click zoom (prevents accidental zoom)
 * - Sets cursor to crosshair for precision
 * - Keeps scroll wheel and touch zoom enabled
 *
 * When not editing:
 * - Restores all default map interactions
 * - Resets cursor to default
 *
 * Usage:
 * ```tsx
 * <MapContainer>
 *   <MapInteractionController isEditing={isDrawingPolygon} />
 *   {other map components}
 * </MapContainer>
 * ```
 */
export function MapInteractionController({ isEditing }: MapInteractionControllerProps): null {
  const map = useMap();

  useEffect(() => {
    if (isEditing) {
      // In edit mode: disable double-click zoom
      map.doubleClickZoom.disable();
      // Keep scrollWheelZoom enabled
      // Keep touchZoom enabled for mobile
      // Dragging is managed by SpacebarPanController

      // Set cursor to crosshair in edit mode
      map.getContainer().style.cursor = 'crosshair';

      return () => {
        map.doubleClickZoom.enable();
        map.getContainer().style.cursor = '';
      };
    } else {
      // Re-enable all interactions when not editing
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();
      map.getContainer().style.cursor = '';
    }
  }, [map, isEditing]);

  return null;
}
